import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_CONFIG } from '../../utils/apiConfig';
import { toast } from 'react-toastify';
import './StaffMessages.css';

const StaffMessages = () => {
  const { currentUser, userProfile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchMessages = useCallback(async () => {
    try {
      if (!currentUser) return;
      
      const idToken = await currentUser.getIdToken(true);
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/marketing/staff-messages/${currentUser.uid}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setUnreadCount(data.unreadCount || 0);
        console.log('✅ StaffMessages: Successfully loaded messages:', data);
      } else {
        console.error('❌ StaffMessages: Failed to fetch messages - Status:', response.status);
        if (response.status === 500) {
          console.log('🔧 StaffMessages: Server error - this might be normal if no messages exist yet');
          // Set empty state for 500 errors (likely no messages collection exists)
          setMessages([]);
          setUnreadCount(0);
        } else {
          toast.error(`Failed to load messages (${response.status})`);
        }
      }
    } catch (error) {
      console.error('❌ StaffMessages: Error fetching messages:', error);
      if (error.message.includes('500')) {
        console.log('🔧 StaffMessages: 500 error caught - setting empty state');
        // Handle 500 errors gracefully
        setMessages([]);
        setUnreadCount(0);
      } else {
        toast.error('Failed to load messages');
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && userProfile) {
      fetchMessages();
    }
  }, [currentUser, userProfile, fetchMessages]);

  const markAsRead = async (messageId) => {
    try {
      const idToken = await currentUser.getIdToken(true);
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/marketing/mark-message-read/${messageId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Update local state
        setMessages(prev => prev.map(msg => 
          msg.id === messageId ? { ...msg, isRead: true, status: 'read' } : msg
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
        toast.success('Message marked as read');
      }
    } catch (error) {
      console.error('Error marking message as read:', error);
      toast.error('Failed to mark message as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      const idToken = await currentUser.getIdToken(true);
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/marketing/mark-all-read/${currentUser.uid}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => prev.map(msg => ({ ...msg, isRead: true, status: 'read' })));
        setUnreadCount(0);
        toast.success(`Marked ${data.updatedCount} messages as read`);
      }
    } catch (error) {
      console.error('Error marking all messages as read:', error);
      toast.error('Failed to mark all messages as read');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="staff-messages-container">
        <div className="loading">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="staff-messages-container">
      <div className="messages-header">
        <h2>My Messages</h2>
        <div className="message-stats">
          <span className="unread-count">
            {unreadCount} unread message{unreadCount !== 1 ? 's' : ''}
          </span>
          {unreadCount > 0 && (
            <button className="mark-all-read-btn" onClick={markAllAsRead}>
              Mark All as Read
            </button>
          )}
        </div>
      </div>

      {messages.length === 0 ? (
        <div className="no-messages">
          <p>No messages yet.</p>
        </div>
      ) : (
        <div className="messages-list">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`message-item ${!message.isRead ? 'unread' : ''}`}
            >
              <div className="message-header">
                <div className="sender-info">
                  <strong>{message.senderName}</strong>
                  <span className="message-time">{formatDate(message.createdAt)}</span>
                </div>
                {!message.isRead && (
                  <button 
                    className="mark-read-btn"
                    onClick={() => markAsRead(message.id)}
                  >
                    Mark as Read
                  </button>
                )}
              </div>
              <div className="message-content">
                {message.message}
              </div>
              {message.isRead && (
                <div className="read-status">
                  ✓ Read {message.readAt ? `at ${formatDate(message.readAt)}` : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StaffMessages;
